import { getI18n } from '@/plugins/i18n'

import { useWebSiteStore } from '@/stores/web-site.store'

import { useProductService } from '@/services/product.service'
import { minutesToHHmm } from '@/lib/utils'

import * as dataGridColumn from '@/composables/data-grid-column'

export const useColumns = (supplierBrand = {}, keepDescription, currency, documentRowType, worksheetCustomer = {}, supersessionId = {}, documentRowProductPurchaseFamily = {}, includesVat = {}, productUnitOfMeasure = {}, laborPriceContext = {}) => {
  const i18n = getI18n()
  const { setTotalVatValue, setTotalNoVatValue, setPriceVatValue } = useProductService()
  const { useRoundedTotalColumn, useRoundingColumn, useRoundingPercentageColumn, useRoundingDocumentTotalVatColumn } = dataGridColumn.useRoundingExpenseColumn(documentRowProductPurchaseFamily, includesVat)

  const webSiteStore = useWebSiteStore()

  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '6rem',
      visibleIndex: 1,
      fixed: false
    },
    dataGridColumn.useRowNumberColumn({width: '8rem'}),
    dataGridColumn.useDocumentRowTypeColumn({
      editCellTemplate: 'documentRowType',
      cellTemplate: 'documentRowTypeCellTemplate',
      productUnitOfMeasure: productUnitOfMeasure,
    }),
    dataGridColumn.useProcessingTypeColumn(),
    dataGridColumn.useSupplierBrandColumn({ supplierBrand, documentRowType }),
    ...dataGridColumn.useProductColumns({ supplierBrand, keepDescription, customer: worksheetCustomer, supersessionId, productUnitOfMeasure, priceContext: laborPriceContext }),
    dataGridColumn.useDescriptionColumn({ validationRules: [{ type: 'required' }] }),
    dataGridColumn.useUnitOfMeasureColumn({
      showClearButton: false,
      productUnitOfMeasure: productUnitOfMeasure,
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const type = e.data.document_row_type
            const isRequiredType = type === webSiteStore.documentRowTypesEnum.PRODUCT || type === webSiteStore.documentRowTypesEnum.FREE
            const hasValue = !!e.value
            return hasValue || !isRequiredType
          }
        }
      ]
    }),
    dataGridColumn.useQuantityColumn(),
    dataGridColumn.usePriceColumn({
      dataField: 'price_vat',
      caption: i18n.t('common.unitPriceVat'),
      currency,
      setCellValue: (newData, value, currentRowData) => {
        setPriceVatValue(newData, value, currentRowData)
      }
    }),
    {
      dataField: 'total_discount',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useTotalVatColumn({ currency, setCellValue: (newData, value, currentRowData) => { setTotalVatValue(newData, value, currentRowData, includesVat.value) } }),
    dataGridColumn.useProcessingColumn(),
    dataGridColumn.useProcessingParentColumn(),
    dataGridColumn.useMechanicsColumn(),
    dataGridColumn.useCostColumn({ visible: false, showInColumnChooser: false, validationRules: []}),
    dataGridColumn.useCostValorizationColumn({ visible: false, showInColumnChooser: false, priceContext: laborPriceContext }),
    {
      dataField: 'quote.id',
      caption: i18n.t('common.quoteRow'),
      width: '8rem',
      editCellTemplate: 'quoteRowRef',
      cellTemplate: 'quoteRowRef',
      alignment: 'left',
      visible: false,
      showInColumnChooser: true,
    },
    dataGridColumn.useIsMovementColumn(),
    dataGridColumn.usePriceColumn({
      dataField: 'product_cost',
      caption: i18n.t('common.cost'),
      currency,
      validationRules: [],
      visible: false,
      showInColumnChooser: false
    }),
    dataGridColumn.usePriceColumn({ dataField: 'product_price', caption: i18n.t('common.listing'), currency, validationRules: [], visible: false, showInColumnChooser: false }),
    dataGridColumn.usePriceColumn({ visible: false, caption: i18n.t('common.unitPriceNoVat'), currency }),
    dataGridColumn.useDiscountCategoryColumn({ visible: false, showInColumnChooser: false }),
    ...dataGridColumn.useVatTypeColumns({
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const type = e.data.document_row_type
            const isRequiredType = type === webSiteStore.documentRowTypesEnum.PRODUCT || type === webSiteStore.documentRowTypesEnum.FREE || type === webSiteStore.documentRowTypesEnum.LABOR || type === webSiteStore.documentRowTypesEnum.EXPENSE || type === webSiteStore.documentRowTypesEnum.SERVICE
            const hasValue = !!e.value
            return hasValue || !isRequiredType
          }
        }
      ]
    }),
    dataGridColumn.useTotalNoVatColumn({ visible: false, showInColumnChooser: true, currency, setCellValue: (newData, value, currentRowData) => { setTotalNoVatValue(newData, value, currentRowData) } }),
    dataGridColumn.useNotesColumn({ visible: false, showInColumnChooser: true }),
    dataGridColumn.useDiscountColumn({ visible: false, includesVat: includesVat.value }),
    dataGridColumn.useDiscount2Column({ visible: false, includesVat: includesVat.value }),
    dataGridColumn.useDiscount3Column({ visible: false, includesVat: includesVat.value }),
    dataGridColumn.useChargePercentageColumn({ visible: false, caption: i18n.t('common.chargePercentageAlt') }),
    dataGridColumn.useDepositColumn({ visible: false, showInColumnChooser: false, validationRules: []}),
    dataGridColumn.useDepositColumn({
      dataField: 'garage',
      caption: i18n.t('common.garage'),
      visible: false,
      showInColumnChooser: false,
      validationRules: []}
    ),
    {
      caption: i18n.t('common.timeEquivalent'),
      dataField: 'timeEquivalent',
      editCellTemplate: 'timeEquivalentEditor',
      editorOptions: { readOnly: true },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.actualDuration'),
      dataField: 'actual_duration',
      dataType: 'number',
      width: '5rem',
      format: minutesToHHmm,
      calculateDisplayValue: rowData => rowData.partial_actual_duration ?? rowData.actual_duration,
      visible: false,
      showInColumnChooser: false
    },
    {
      caption: i18n.t('common.suggestedTime'),
      dataField: 'suggested_time',
      dataType: 'number',
      format: minutesToHHmm,
      calculateDisplayValue: rowData => rowData.partial_suggested_time ?? rowData.suggested_time,
      editorOptions: { readOnly: true, format: minutesToHHmm },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.operationCode'),
      dataField: 'operationCode',
      editorOptions: { readOnly: true },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.recognizedTime'),
      dataField: 'recognizedTime',
      editorOptions: { readOnly: true },
      visible: false,
      showInColumnChooser: false,
    },
    useRoundedTotalColumn({ currency }),
    useRoundingColumn({ currency }),
    useRoundingPercentageColumn(),
    useRoundingDocumentTotalVatColumn({ currency }),
  ]
}
